import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Button, Modal, message, Input } from 'antd';
import { EyeOutlined, CheckOutlined, ClockCircleOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import AuditDetailModal from './inventoryAudit/AuditDetailModal';
import {
  formatDate,
  formatVND,
  mapStatusToVN,
  mapTechnicalConditionToVN,
  mapUnitToVN,
  isFirstAudit,
  getPreviousAudit,
  hasFieldChanged,
  parseSnapshot,
  getLocationName,
  type AuditRecord,
  type AccessoryItem,
  type Location,
  type Company,
  type Category,
  type Supplier,
  type Employee
} from '../utils/auditHelpers';

interface AuditHistoryTableProps {
  assetId: number;
  locations: Location[];
}

// Component để hiển thị thông số kỹ thuật đẹp hơn
const TechnicalSpecificationsDisplay: React.FC<{ 
  specs: any; 
  auditId: number; 
  auditHistory: AuditRecord[] 
}> = ({ specs, auditId, auditHistory }) => {
  if (!specs || typeof specs !== 'object') return <span>—</span>;
  
  const specsArray = Object.entries(specs);
  if (specsArray.length === 0) return <span>—</span>;
  
  // Lấy kiểm kê trước đó để so sánh
  const getPreviousAuditForSpecs = (currentId: number): AuditRecord | undefined => {
    return getPreviousAudit(currentId, auditHistory);
  };
  
  const previousAudit = getPreviousAuditForSpecs(auditId);
  const previousSpecs = previousAudit?.snapshot ? 
    parseSnapshot(previousAudit.snapshot)?.technical_specifications : null;
  
  // Chia thành 2 cột, mỗi cột tối đa 3 hàng
  const leftColumn = specsArray.slice(0, 3);
  const rightColumn = specsArray.slice(3, 6);
  
  const renderSpecItem = (key: string, value: any, index: number, isLast: boolean) => {
    // Kiểm tra xem có thay đổi không
    const hasChanged = !isFirstAudit(auditId, auditHistory) && previousSpecs && previousSpecs[key] !== value;
    
    return (
      <div key={key} style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        marginBottom: isLast ? '0' : '4px',
        paddingBottom: isLast ? '0' : '4px',
        borderBottom: isLast ? 'none' : '1px solid #dee2e6'
      }}>
        <span style={{ 
          fontWeight: '500', 
          color: '#495057',
          minWidth: '100px'
        }}>
          {key}:
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ 
            color: '#6c757d',
            textAlign: 'right',
            flex: 1,
            fontWeight: hasChanged ? 500 : 400,
            backgroundColor: hasChanged ? '#fff3cd' : 'transparent',
            padding: hasChanged ? '2px 4px' : '0',
            borderRadius: hasChanged ? '2px' : '0'
          }}>
            {value ? String(value) : '—'}
          </span>
          {isFirstAudit(auditId, auditHistory) && (
            <span style={{ 
              fontSize: '12px', 
              color: '#27ae60', 
              marginLeft: '8px'
            }}>
              ✨ Mới
            </span>
          )}
          {hasChanged && (
            <span style={{ 
              fontSize: '12px', 
              color: '#27ae60', 
              marginLeft: '8px'
            }}>
              ✨ Thay đổi
            </span>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div style={{ 
      backgroundColor: '#f8f9fa', 
      border: '1px solid #e9ecef',
      borderRadius: '6px',
      padding: '8px 12px',
      fontSize: '13px'
    }}>
      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Cột trái */}
        <div style={{ flex: 1 }}>
          {leftColumn.map(([key, value], index) => 
            renderSpecItem(key, value, index, index === leftColumn.length - 1)
          )}
        </div>
        
        {/* Cột phải */}
        {rightColumn.length > 0 && (
          <div style={{ flex: 1 }}>
            {rightColumn.map(([key, value], index) => 
              renderSpecItem(key, value, index, index === rightColumn.length - 1)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AuditHistoryTable: React.FC<AuditHistoryTableProps> = ({ assetId, locations }) => {
  // State
  const [auditHistory, setAuditHistory] = useState<AuditRecord[]>([]);
  const [loadingAuditHistory, setLoadingAuditHistory] = useState(false);
  const [selectedAuditDetail, setSelectedAuditDetail] = useState<AuditRecord | null>(null);
  const [showAuditDetailModal, setShowAuditDetailModal] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editingNotes, setEditingNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [approvingDetail, setApprovingDetail] = useState<AuditRecord | null>(null);
  const [auditDocumentCode, setAuditDocumentCode] = useState('');
  const [approvingLoading, setApprovingLoading] = useState(false);
  
  // State cho mapping data
  const [companies, setCompanies] = useState<Company[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  // Helper function để fetch audit history
  const fetchAuditHistory = useCallback(async () => {
    if (!assetId) return;
    
    setLoadingAuditHistory(true);
    try {
      const response: any = await apiClient.getAssetAuditHistory(assetId);
      const auditData = response.data || response;
      setAuditHistory(auditData);
    } catch (error) {
      console.error('Lỗi fetch audit history:', error);
      setAuditHistory([]);
    } finally {
      setLoadingAuditHistory(false);
    }
  }, [assetId]);

  // Effects
  useEffect(() => {
    fetchAuditHistory();
  }, [fetchAuditHistory]);

  // Load mapping data khi mở modal
  useEffect(() => {
    if (showAuditDetailModal) {
      const loadMappingData = async () => {
        try {
          const [companiesRes, categoriesRes, suppliersRes, employeesRes] = await Promise.all([
            apiClient.getCompanies(),
            apiClient.getAssetCategories(),
            apiClient.getSuppliers(),
            apiClient.getEmployees()
          ]);
          
          setCompanies(companiesRes as Company[]);
          setCategories(categoriesRes as Category[]);
          setSuppliers(suppliersRes as Supplier[]);
          setEmployees(employeesRes as Employee[]);
        } catch (error) {
          console.error('Lỗi load mapping data:', error);
        }
      };
      
      loadMappingData();
    }
  }, [showAuditDetailModal]);

  // Helper functions để map ID thành tên
  const getCompanyName = useCallback((companyId: number | undefined | null): string => {
    if (!companyId) return '—';
    return companies.find(c => c.id === companyId)?.name || '—';
  }, [companies]);

  const getCategoryName = useCallback((categoryId: number | undefined | null): string => {
    if (!categoryId) return '—';
    return categories.find(c => c.id === categoryId)?.name || '—';
  }, [categories]);

  const getSupplierName = useCallback((supplierId: number | undefined | null): string => {
    if (!supplierId) return '—';
    return suppliers.find(s => s.id === supplierId)?.name || '—';
  }, [suppliers]);

  const getEmployeeName = useCallback((employeeId: number | undefined | null): string => {
    if (!employeeId) return '—';
    return employees.find(e => e.id === employeeId)?.full_name || String(employeeId);
  }, [employees]);

  // Hàm để lưu ghi chú
  const handleSaveNotes = async () => {
    if (!selectedAuditDetail) return;
    
    setSavingNotes(true);
    try {
      await apiClient.patch(`/inventory-audit-details/${selectedAuditDetail.id}`, {
        notes: editingNotes
      });
      
      // Cập nhật local state
      setSelectedAuditDetail({
        ...selectedAuditDetail,
        notes: editingNotes
      });
      
      // Cập nhật trong auditHistory
      setAuditHistory(prev => 
        prev.map(audit => 
          audit.id === selectedAuditDetail.id 
            ? { ...audit, notes: editingNotes }
            : audit
        )
      );
      
      setIsEditingNotes(false);
      message.success('Lưu ghi chú thành công');
    } catch (error) {
      message.error('Lỗi khi lưu ghi chú');
      console.error('Lỗi lưu ghi chú:', error);
    } finally {
      setSavingNotes(false);
    }
  };

  // Hàm để duyệt kiểm kê
  const handleApproveAudit = async (auditId: number, auditDocumentCode: string) => {
    try {
      await apiClient.post(`/inventory-audit-details/${auditId}/approve`, {
        audit_document_code: auditDocumentCode
      });
      fetchAuditHistory(); // Refresh data
    } catch (error) {
      throw error; // Re-throw để AuditDetailModal xử lý
    }
  };

  const handleApproveFromList = async () => {
    if (!approvingDetail || !auditDocumentCode.trim()) {
      message.error('Vui lòng nhập mã hồ sơ kiểm kê');
      return;
    }

    setApprovingLoading(true);
    try {
      await apiClient.approveAuditDetail(approvingDetail.id, auditDocumentCode.trim());
      setApprovingDetail(null);
      setAuditDocumentCode('');
      await fetchAuditHistory(); // Reload data
      message.success('Duyệt kiểm kê thành công');
    } catch (error) {
        message.error('Lỗi khi duyệt kiểm kê');
    } finally {
      setApprovingLoading(false);
    }
  };

  // Helper function để format giá trị dựa trên key
  const formatFieldValue = (key: string, value: any): string | React.ReactElement => {
    if (value === null || value === undefined || value === '') return '—';
    
    // Xử lý các trường ngày
    if (key.includes('date') || key.includes('Date')) {
      return formatDate(value);
    }
    
    // Xử lý các trường ID cần map
    switch (key) {
      case 'company_id':
        return getCompanyName(value);
      case 'category_id':
        return getCategoryName(value);
      case 'supplier_id':
        return getSupplierName(value);
      case 'assigned_to_employee_id':
        return getEmployeeName(value);
      case 'location_id':
        return getLocationName(value, locations);
      case 'purchase_price':
      case 'purchase_cost':
        return formatVND(value);
      case 'warranty_months':
      case 'audit_cycle_months':
        return value ? String(value) : '—';
      case 'technical_specifications':
        if (typeof value === 'object') {
          return <span>Thông số kỹ thuật</span>; // Placeholder, sẽ được xử lý riêng
        }
        return String(value);
      case 'status':
        return mapStatusToVN(String(value));
      case 'technical_condition':
        return mapTechnicalConditionToVN(String(value));
      default:
        return String(value);
    }
  };

  // Helper function để hiển thị giá trị với icon "Mới" nếu cần
  const renderValueWithNewIcon = (value: string | React.ReactElement, auditId: number, fieldName: string, isAssetInfo: boolean = false, rawValue?: any): React.ReactElement => {
    const isNew = isFirstAudit(auditId, auditHistory);
    
    // Chỉ so sánh thay đổi cho thông tin tài sản, không áp dụng cho thông tin kiểm kê
    let hasChanged = false;
    if (isAssetInfo) {
      const previousAudit = getPreviousAudit(auditId, auditHistory);
      if (previousAudit && previousAudit.snapshot) {
        const previousRawValue = parseSnapshot(previousAudit.snapshot)[fieldName];
        const currentRawValue = rawValue !== undefined ? rawValue : parseSnapshot(selectedAuditDetail?.snapshot)[fieldName];
        hasChanged = previousRawValue !== currentRawValue;
      }
    }
    
    if (typeof value === 'string') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ 
            fontWeight: hasChanged ? 500 : 400,
            backgroundColor: hasChanged ? '#fff3cd' : 'transparent',
            padding: hasChanged ? '2px 4px' : '0',
            borderRadius: hasChanged ? '2px' : '0'
          }}>
            {value}
          </span>
          {(isNew || hasChanged) && (
            <span style={{ 
              fontSize: '12px', 
              color: '#27ae60', 
              marginLeft: '8px'
            }}>
              ✨ {isNew ? 'Mới' : 'Thay đổi'}
            </span>
          )}
        </div>
      );
    }
    
    // Xử lý ReactElement (như TechnicalSpecificationsDisplay)
    if (React.isValidElement(value)) {
      return (
        <div>
          {value}
          {(isNew || hasChanged) && (
            <div style={{ 
              fontSize: '12px', 
              color: '#27ae60', 
              marginTop: '4px'
            }}>
              ✨ {isNew ? 'Mới' : 'Thay đổi'}
            </div>
          )}
        </div>
      );
    }
    
    return value;
  };

  // Helper function để hiển thị ngày kiểm kê gần nhất
  const getLastAuditDateDisplay = (snap: any, selectedAuditDetail: any): string => {
    // Lấy last_audit_date từ snapshot (đây là ngày tạo/chỉnh sửa tài sản tại thời điểm tạo snapshot)
    const lastAuditDate = snap.last_audit_date;
    
    // Nếu không có last_audit_date
    if (!lastAuditDate) {
      return 'Chưa có kiểm kê trước';
    }
    
    return formatDate(lastAuditDate);
  };

  // Memoized values
  const auditColumns = useMemo(() => [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Ngày kiểm kê',
      dataIndex: 'audit_date',
      key: 'audit_date',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Kết quả',
      dataIndex: 'result',
      key: 'result',
      render: (result: string) => {
        // Backend đã transform thành tiếng Việt rồi
        return result || '—';
      },
    },
    {
      title: 'Tình trạng',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        // Backend đã transform thành tiếng Việt rồi
        return status || '—';
      },
    },
    {
      title: 'Loại kiểm kê',
      key: 'type',
      render: (_: any, record: AuditRecord) => {
        // Lấy từ field type đã được transform
        if (record.type && record.type !== 'null' && record.type !== 'undefined') {
          return record.type; // Backend đã transform thành tiếng Việt rồi
        }
        
        // Fallback: nếu không có type thì hiển thị dấu gạch
        return '—';
      },
    },
    {
      title: 'Hành động',
      key: 'actions',
      render: (_: any, record: AuditRecord) => {
        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
              type="link" 
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedAuditDetail(record);
                setShowAuditDetailModal(true);
                setIsEditingNotes(false);
                setEditingNotes(record.notes || '');
              }}
              title="Xem chi tiết"
            />
            {/* Ẩn icon duyệt nếu là kiểm kê định kỳ, kiểm kê điều chuyển hoặc kiểm kê thanh lý */}
            {record.type !== 'Kiểm kê định kỳ' && record.type !== 'Kiểm kê điều chuyển' && record.type !== 'Kiểm kê thanh lý' && (
              record.status === 'Chờ duyệt' ? (
                <Button 
                  type="link" 
                  size="small"
                  icon={<ClockCircleOutlined />}
                  onClick={() => setApprovingDetail(record)}
                  title="Duyệt kiểm kê"
                  style={{ color: '#faad14' }}
                />
              ) : record.status === 'Đã duyệt' ? (
                <Button 
                  type="link" 
                  size="small"
                  icon={<CheckOutlined />}
                  title="Đã duyệt"
                  style={{ color: '#52c41a' }}
                  disabled
                />
              ) : null
            )}
          </div>
        );
      },
    },
  ], [locations]);

  const renderAccessoriesTable = useCallback(() => {
    if (!selectedAuditDetail?.accessories_snapshot?.length) return null;

    const accessoriesColumns = [
      {
        title: 'Tên vật tư',
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          
          return (
            <div>
              <span style={{ fontWeight: previousItem ? 400 : 500 }}>
                {text}
              </span>
              {!previousItem && (
                <span style={{ fontSize: 12, color: '#27ae60', marginLeft: 8 }}>
                  ✨ Mới
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: 'Số lượng',
        dataIndex: 'quantity',
        key: 'quantity',
        render: (value: number, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          const hasChange = previousItem && previousItem.quantity !== value;
          
          return (
            <span style={{ 
              fontWeight: hasChange ? 500 : 400,
              backgroundColor: hasChange ? '#fff3cd' : 'transparent',
              padding: hasChange ? '2px 4px' : '0',
              borderRadius: hasChange ? '2px' : '0'
            }}>
              {value}
            </span>
          );
        },
      },
      {
        title: 'Đơn vị',
        dataIndex: 'unit',
        key: 'unit',
        render: (value: string, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          const hasChange = previousItem && previousItem.unit !== value;
          
          return (
            <span style={{ 
              fontWeight: hasChange ? 500 : 400,
              backgroundColor: hasChange ? '#fff3cd' : 'transparent',
              padding: hasChange ? '2px 4px' : '0',
              borderRadius: hasChange ? '2px' : '0'
            }}>
              {value ? mapUnitToVN(value) : '—'}
            </span>
          );
        },
      },
      {
        title: 'Đơn giá',
        dataIndex: 'price',
        key: 'price',
        render: (value: number, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          const hasChange = previousItem && previousItem.price !== value;
          
          return (
            <span style={{ 
              fontWeight: hasChange ? 500 : 400,
              backgroundColor: hasChange ? '#fff3cd' : 'transparent',
              padding: hasChange ? '2px 4px' : '0',
              borderRadius: hasChange ? '2px' : '0'
            }}>
              {value ? formatVND(value) : '—'}
            </span>
          );
        },
      },
      {
        title: 'Thành tiền',
        key: 'total_price',
        render: (_: any, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          const currentTotal = record.total_price || (record.price && record.quantity ? record.price * record.quantity : 0);
          const previousTotal = previousItem?.total_price || (previousItem?.price && previousItem?.quantity ? previousItem.price * previousItem.quantity : 0);
          const hasChange = previousItem && previousTotal !== currentTotal;
          
          return (
            <span style={{ 
              fontWeight: hasChange ? 500 : 400,
              backgroundColor: hasChange ? '#fff3cd' : 'transparent',
              padding: hasChange ? '2px 4px' : '0',
              borderRadius: hasChange ? '2px' : '0'
            }}>
              {formatVND(currentTotal)}
            </span>
          );
        },
      },
      {
        title: 'Ngày mua',
        dataIndex: 'purchase_date',
        key: 'purchase_date',
        render: (value: string, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          const hasChange = previousItem && previousItem.purchase_date !== value;
          
          return (
            <span style={{ 
              fontWeight: hasChange ? 500 : 400,
              backgroundColor: hasChange ? '#fff3cd' : 'transparent',
              padding: hasChange ? '2px 4px' : '0',
              borderRadius: hasChange ? '2px' : '0'
            }}>
              {value ? formatDate(value) : '—'}
            </span>
          );
        },
      },
      {
        title: 'Hồ sơ mua',
        dataIndex: 'purchase_document',
        key: 'purchase_document',
        render: (value: string, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          const hasChange = previousItem && previousItem.purchase_document !== value;
          
          return (
            <span style={{ 
              fontWeight: hasChange ? 500 : 400,
              backgroundColor: hasChange ? '#fff3cd' : 'transparent',
              padding: hasChange ? '2px 4px' : '0',
              borderRadius: hasChange ? '2px' : '0'
            }}>
              {value || '—'}
            </span>
          );
        },
      },
      {
        title: 'Ghi chú',
        dataIndex: 'notes',
        key: 'notes',
        render: (value: string, record: AccessoryItem) => {
          const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
          const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
          const hasChange = previousItem && previousItem.notes !== value;
          
          return (
            <span style={{ 
              fontWeight: hasChange ? 500 : 400,
              backgroundColor: hasChange ? '#fff3cd' : 'transparent',
              padding: hasChange ? '2px 4px' : '0',
              borderRadius: hasChange ? '2px' : '0'
            }}>
              {value || '—'}
            </span>
          );
        },
      },
    ];

    return (
      <div>
        <Table
          dataSource={selectedAuditDetail.accessories_snapshot}
          columns={accessoriesColumns}
          pagination={false}
          size="small"
          rowClassName={(record) => {
            const previousAudit = getPreviousAudit(selectedAuditDetail.id, auditHistory);
            const previousItem = previousAudit?.accessories_snapshot?.find((p: AccessoryItem) => p.name === record.name);
            if (previousItem) {
              const hasChanges = previousItem.quantity !== record.quantity || 
                               previousItem.price !== record.price;
              return hasChanges ? 'highlight-row' : '';
            }
            return previousAudit ? 'highlight-row' : '';
          }}
        />
      </div>
    );
  }, [selectedAuditDetail, auditHistory]);

  return (
    <>
        <div style={{ maxHeight: '400px' }}>
          <Table
            dataSource={auditHistory}
            loading={loadingAuditHistory}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ y: 350 }}
            sticky={{ offsetHeader: 0 }}
            columns={auditColumns}
          />
        </div>
      <AuditDetailModal
        visible={showAuditDetailModal}
        onClose={() => setShowAuditDetailModal(false)}
        auditDetail={selectedAuditDetail}
                      auditHistory={auditHistory} 
        locations={locations}
        companies={companies}
        categories={categories}
        suppliers={suppliers}
        employees={employees}
        onSaveNotes={handleSaveNotes}
        onApproveAudit={handleApproveAudit}
        showApproveButton={true}
      />

      {/* Modal duyệt kiểm kê */}
      <Modal
        title="Duyệt kiểm kê"
        open={!!approvingDetail}
        onCancel={() => {
          setApprovingDetail(null);
          setAuditDocumentCode('');
        }}
        onOk={handleApproveFromList}
        confirmLoading={approvingLoading}
        okText="Duyệt"
        cancelText="Hủy"
        okButtonProps={{
          disabled: !auditDocumentCode.trim()
        }}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Vui lòng nhập mã hồ sơ kiểm kê để duyệt.
          </p>
          <Input
            placeholder="Nhập mã hồ sơ kiểm kê..."
            value={auditDocumentCode}
            onChange={(e) => setAuditDocumentCode(e.target.value)}
            onPressEnter={handleApproveFromList}
            autoFocus
          />
        </div>
      </Modal>
    </>
  );
};

export default AuditHistoryTable; 